import { CreditCard } from 'lucide-react'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { useBillingSummary, useOverages } from '@/hooks/use-billing'
import { formatNumber } from '@/lib/format'
import { OveragePanels } from '@/pages/billing/overage-panels'
import { TierBreakdownTable } from '@/pages/billing/tier-breakdown-table'

const COST_PER_EMAIL_EUR = 0.00005

export function BillingPage(): React.JSX.Element {
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useBillingSummary()
  const { data: overages, isLoading: overagesLoading, error: overagesError } = useOverages()

  const isLoading = summaryLoading || overagesLoading
  const error = summaryError || overagesError

  if (isLoading) {
    return (
      <div className="space-y-6">
        <BillingHeader />
        <LoadingPage message="Loading billing data..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <BillingHeader />
        <div className="rounded-xl border border-danger/30 bg-surface-0 p-12 text-center">
          <p className="text-danger">Failed to load billing data. Please try again.</p>
        </div>
      </div>
    )
  }

  const platformCost = summary ? summary.total_emails * COST_PER_EMAIL_EUR : 0

  return (
    <div className="space-y-6">
      <BillingHeader />

      {/* Top row: metric cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="MRR"
          value={summary ? `$${summary.mrr.toLocaleString()}` : '$0'}
          variant="success"
        />
        <MetricCard
          label="Total Emails"
          value={summary ? formatNumber(summary.total_emails) : '0'}
          sub="This period"
        />
        <MetricCard
          label="Override Accounts"
          value={summary ? String(summary.override_count) : '0'}
          sub="Custom limits active"
          variant={summary && summary.override_count > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Platform Cost"
          value={`EUR ${platformCost.toFixed(2)}`}
          sub="Infrastructure estimate"
        />
      </div>

      {/* Tier breakdown */}
      {summary && <TierBreakdownTable data={summary.accounts_by_tier} />}

      {/* Overage panels */}
      {overages && (
        <OveragePanels approaching={overages.approaching} inOverage={overages.in_overage} />
      )}
    </div>
  )
}

function BillingHeader(): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <CreditCard className="h-6 w-6 text-admin-500" />
      <h1 className="text-2xl font-semibold text-text-primary">Billing & Revenue</h1>
    </div>
  )
}
